import { useCallback, useRef, useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { useFirmaAccountId, useFirmaContext } from '@/contexts/FirmaContext'
import { useAutoPromoteToast } from '@/hooks/useAutoPromoteToast'
import Tabs from '@/components/Tabs'
import { ArrowLeft } from 'lucide-react'
import ErrorAlert from '@/components/ErrorAlert'
import Spinner from '@/components/Spinner'
import { useRightPanel } from '@/contexts/RightPanelContext'
import FirmaOverview from './FirmaOverview'
import FirmaStammdaten from './FirmaStammdaten'
import FirmaAnsprechpartner from './FirmaAnsprechpartner'
import FirmaTeilnehmer, { type TnFilter } from './FirmaTeilnehmer'
import FirmaRechnungen from './FirmaRechnungen'
import FirmaDualMatch from './FirmaDualMatch'
import FirmaVerfuegbarkeit from './FirmaVerfuegbarkeit'

const VALID_TABS = ['uebersicht', 'stammdaten', 'ansprechpartner', 'teilnehmer', 'verfuegbarkeit', 'rechnungen', 'dualmatch']

export default function FirmaPage() {
  const accountId = useFirmaAccountId()
  const { canRead } = useFirmaContext()
  const navigate = useNavigate()
  const location = useLocation()

  const hashTab = location.hash.replace('#', '')
  // activeTab = Hash, falls valid UND canRead. Sonst Übersicht-Fallback.
  const tabSection: Record<string, string | null> = {
    uebersicht: null,
    stammdaten: 'stammdaten', ansprechpartner: 'ansprechpartner',
    teilnehmer: 'studiengaenge', verfuegbarkeit: 'vorlesungstermine',
    rechnungen: 'rechnungen', dualmatch: 'studiengaenge',
  }
  const requiredSection = tabSection[hashTab]
  const tabAllowed = VALID_TABS.includes(hashTab) && (requiredSection == null || canRead(requiredSection))
  const activeTab = tabAllowed ? hashTab : 'uebersicht'
  const setActiveTab = useCallback((id: string) => {
    // Kein replace — Tab-Wechsel landet als eigener History-Eintrag.
    // Konsistent mit den FirmaNavbar-<Link>s und Quicklink-Widget; Browser-/
    // App-Zurück navigiert dadurch Station-für-Station zurueck statt Tabs zu
    // ueberspringen.
    navigate({ hash: id === 'uebersicht' ? '' : `#${id}` })
  }, [navigate])

  const [tnFilter, setTnFilter] = useState<TnFilter | null>(null)
  const showTeilnehmerFiltered = useCallback((filter?: TnFilter) => {
    setTnFilter(filter ?? null)
    setActiveTab('teilnehmer')
  }, [setActiveTab])

  const queryClient = useQueryClient()
  const logoInputRef = useRef<HTMLInputElement>(null)

  const logoMutation = useMutation({
    mutationFn: (imageBase64: string | null) => api.updateLogo(accountId!, imageBase64),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['firma', accountId] })
    },
  })

  const handleLogoFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) { alert('Die Datei ist zu groß (max. 5 MB).'); return }
    const reader = new FileReader()
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1]
      if (base64) logoMutation.mutate(base64)
    }
    reader.readAsDataURL(file)
    e.target.value = ''
  }, [logoMutation])

  const { data, isError, error, refetch } = useQuery({
    queryKey: ['firma', accountId],
    queryFn: () => api.firma(accountId!),
    enabled: !!accountId,
  })

  // Rechtes Panel
  const { setFirma, clearFirma } = useRightPanel()
  useEffect(() => {
    if (data?.firma && accountId) {
      setFirma(accountId, data.firma.name)
    }
    return () => clearFirma()
  }, [accountId, data?.firma?.name, setFirma, clearFirma])

  // Auto-promote toast — must be called unconditionally (Rules of Hooks)
  useAutoPromoteToast(data?.firma?.name)

  if (isError) {
    return (
      <div className="px-6 py-10">
        <ErrorAlert
          message={error instanceof Error ? error.message : 'Firma nicht gefunden'}
          onRetry={() => refetch()}
        />
      </div>
    )
  }

  const firma = data?.firma
  const primary_contact = data?.primary_contact ?? null
  const ansprechpartner = data?.ansprechpartner ?? []
  const fom_ansprechpartner = data?.fom_ansprechpartner ?? []
  const teilnehmer = data?.teilnehmer ?? []
  const firmenId = firma?.nummer && /^\d+$/.test(firma.nummer) ? Number(firma.nummer) : null

  if (!firma) {
    return <Spinner size="lg" className="min-h-[60vh]" />
  }

  return (
    <div>
      <input ref={logoInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleLogoFile} className="hidden" />

      {/* ── Zurück (echtes Browser-Back) ── */}
      {activeTab !== 'uebersicht' && (
        <div className="sticky top-[114px] z-40 px-6 py-3 bg-[#F7F7F7] border-b border-gray-200">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-fom-600 text-sm font-medium hover:text-fom-700 bg-transparent border-none cursor-pointer px-0"
          >
            <ArrowLeft size={16} />
            Zurück
          </button>
        </div>
      )}

      {/* ── Tabs ── */}
      <Tabs
        sectionTitle={firma.name}
        activeTab={activeTab}
        onTabChange={(id) => { if (id !== 'teilnehmer') setTnFilter(null); setActiveTab(id) }}
        tabs={[
          {
            id: 'uebersicht', label: 'Übersicht', noScroll: true,
            content: <FirmaOverview firma={firma} primaryContact={primary_contact} teilnehmer={teilnehmer} accountId={accountId!} onShowTeilnehmer={showTeilnehmerFiltered} onShowDualMatch={() => setActiveTab('dualmatch')} fomAnsprechpartner={fom_ansprechpartner} />,
          },
          {
            id: 'stammdaten', label: 'Stammdaten',
            content: <FirmaStammdaten firma={firma} primaryContact={primary_contact} accountId={accountId!} />,
          },
          {
            id: 'ansprechpartner', label: 'Ansprechpartner', badge: ansprechpartner.length,
            content: <FirmaAnsprechpartner ansprechpartner={ansprechpartner} accountId={accountId!} />,
          },
          {
            id: 'teilnehmer', label: 'Teilnehmer', badge: teilnehmer.length,
            content: <FirmaTeilnehmer teilnehmer={teilnehmer} accountId={accountId!} filter={tnFilter} onClearFilter={() => setTnFilter(null)} />,
          },
          {
            id: 'verfuegbarkeit', label: 'Verfügbarkeit',
            content: <FirmaVerfuegbarkeit teilnehmer={teilnehmer} accountId={accountId!} />,
          },
          {
            id: 'rechnungen', label: 'Rechnungen',
            content: <FirmaRechnungen firmenId={firmenId} />,
          },
          {
            id: 'dualmatch', label: 'DualMatch', noScroll: true,
            content: <FirmaDualMatch />,
          },
        ].filter(t => {
          // Per-Section-Permissions: Tabs nur sichtbar wenn canRead.
          // Übersicht ist immer sichtbar (Basis-Info).
          // Mapping Tab→Section:
          //   stammdaten → 'stammdaten'
          //   ansprechpartner → 'ansprechpartner'
          //   teilnehmer / dualmatch → 'studiengaenge'
          //   verfuegbarkeit → 'vorlesungstermine'
          //   rechnungen → 'rechnungen'
          switch (t.id) {
            case 'stammdaten': return canRead('stammdaten')
            case 'ansprechpartner': return canRead('ansprechpartner')
            case 'teilnehmer': return canRead('studiengaenge')
            case 'verfuegbarkeit': return canRead('vorlesungstermine')
            case 'rechnungen': return canRead('rechnungen')
            case 'dualmatch': return canRead('studiengaenge')
            default: return true  // uebersicht
          }
        })}
      />
    </div>
  )
}

